import { Button } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Toast from "../../../components/Toast";
import AuthLayout from "../components/AuthLayout";
import { useAuth } from "../hooks/useAuth";
import { userService } from "../services/userService";

/**
 * Email verification page component
 * Handles verification of user email using code from URL parameters
 */
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Extract code and uid from URL
  const code = searchParams.get("code");
  const uid = searchParams.get("uid");

  // Handle manual verification code entry
  const [manualCode, setManualCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  useEffect(() => {
    // If code and uid are provided in URL, verify automatically
    if (code && uid) {
      verifyEmail(code, uid);
    }
  }, [code, uid]);

  // Countdown after successful verification
  useEffect(() => {
    let timer;
    if (verified && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (verified && countdown === 0) {
      navigate("/signin");
    }
    return () => clearTimeout(timer);
  }, [verified, countdown, navigate]);

  const verifyEmail = async (verificationCode, userId) => {
    try {
      setVerifying(true);
      setError(null);

      const response = await userService.verifyEmail(verificationCode, userId);

      if (response.verified) {
        setVerified(true);
      } else {
        setError("Email verification failed. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Email verification failed. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode || !uid) {
      setError("Verification code and user ID are required.");
      return;
    }
    setSubmitting(true);
    await verifyEmail(manualCode, uid);
    setSubmitting(false);
  };

  const handleResendVerification = async () => {
    if (!uid) {
      setError("User ID is required to resend verification email.");
      return;
    }

    try {
      setVerifying(true);
      setError(null);

      await userService.resendVerificationEmail(uid);

      setError(null);
      setToastMessage(
        "Verification email has been resent. Please check your inbox.",
      );
      setToastType("success");
      setToastOpen(true);
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to resend verification email. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <AuthLayout>
      <div className="max-w-md mx-auto">
        <Toast
          type={toastType}
          open={toastOpen}
          setOpen={setToastOpen}
          className="mb-4"
        >
          {toastMessage}
        </Toast>
        <h1 className={`text-3xl ${theme.text.primary} mb-6`}>
          Email Verification
        </h1>

        {error && (
          <div className="mb-4">
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {verified ? (
          <div className="text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">
                Email Verified Successfully!
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Your email has been verified. You will be redirected to the
                login page in {countdown} seconds.
              </p>
            </div>
            <Button onClick={() => navigate("/signin")} variant="primary">
              Go to Login
            </Button>
          </div>
        ) : (
          <div>
            {verifying ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                <p className="mt-2">Verifying your email...</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Almost There!
                        <br />
                        Your verification code is on its way. Please check your
                        email and enter the code below to continue.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-orange-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-orange-700">
                        Please enter the verification code from your email. If you did not receive it, please check your spam or junk folder. Need a new code? Click below to resend.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleManualSubmit} className="space-y-4">
                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      htmlFor="code"
                    >
                      Verification Code
                    </label>
                    <input
                      id="code"
                      className="form-input w-full"
                      type="text"
                      placeholder="Enter your verification code"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="text-sm underline hover:no-underline"
                      disabled={submitting}
                    >
                      Resend verification email
                    </button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting}
                    >
                      {submitting ? "Verifying..." : "Verify Email"}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
